import React, { useState, useEffect } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Form, Modal, Select, Input, InputNumber, Flex, Checkbox, Alert } from 'antd';
import { NotificationInstance } from 'antd/lib/notification/interface';
import { PlusOutlined } from '@ant-design/icons';

import { useAppDispatch } from '@store/hooks';

import IBaseComponentProps from '@components/base-component-props';

import { getEnumList, toFinanceString } from '@extensions/utils';
import { exception } from '@extensions/notification';
import { userLoaded } from '@store/actions';

import { IUser } from '@entities/user';
import { ISystemSetting } from '@entities/system-setting';
import { ITransaction } from '@entities/transaction';
import { ICancelTransactionParams } from '@entities/cancel-transaction-params';

import { IEnumItem } from '@enums/enum-item';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { ExchangeRateType, enumLabel as rateTypeLabel } from '@enums/exchange-rate-type';
import { ExchangeRateCommissionType } from '@enums/exchange-rate-commission-type';
import { SystemSettingType } from '@enums/system-setting-type';
import { TransactionType } from '@core/enums/transaction-types';

dayjs.extend(utc);

interface ICancelTransactionProps extends IBaseComponentProps {
    userId: string;
    transaction: ITransaction;
    onSave: () => void;
    onCancel: () => void;
    api: NotificationInstance;
}

const CancelTransactionModal = (props: ICancelTransactionProps) => {
    const { userId, transaction, api, onSave, onCancel, serverFetch } = props;

    const { TextArea } = Input;

    const d = useAppDispatch();

    const [form] = Form.useForm();

    const [rateTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateType, rateTypeLabel));
    const [rateCommissionTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateCommissionType));

    const [internalUsdExchangeRate, setInternalUsdExchangeRate] = useState<number | undefined>();
    const [messageInfo, setMessageInfo] = useState<string>();

    const [currentRateType, setCurrentRateType] = useState<ExchangeRateType | undefined>(ExchangeRateType.CentralBank);

    const [user, setUser] = useState<IUser>();

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [addBillRequired, setAddBillRequired] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`users/${userId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`systemSettings/${SystemSettingType.InternalExchangeRate}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения внутреннего курса', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUser(result[0][0]);

                const internalUsdExchangeRateSetting: ISystemSetting = result[0][1];
                setInternalUsdExchangeRate(
                    internalUsdExchangeRateSetting && internalUsdExchangeRateSetting.value ? +internalUsdExchangeRateSetting.value : 0
                );

                if (transaction) {
                    form.setFieldValue('transactionId', transaction.id);

                    if (transaction.currency == Currency.Usd) form.setFieldValue('amount', transaction.amount);

                    let message: any = '';
                    if (transaction.type == TransactionType.Depositing) {
                        message = (
                            <>
                                Для отмены операции <b>"Пополнение"</b>, без изменения суммы клиентского счета, пожалуйста отметьте
                                настройку{' '}
                                <b>"Создать счет"</b>, для выставления клиенту счета на сумму{' '}
                                <b>
                                    {toFinanceString(transaction.amount)} {currencySign(transaction.currency)}
                                </b>
                                .
                                <br />В противном случае, сумма клиентского счета будет уменьшена на сумму{' '}
                                <b style={{ color: 'var(--main-red)' }}>
                                    -{toFinanceString(transaction.amount)} {currencySign(transaction.currency)}
                                </b>
                                ;
                            </>
                        );
                    }
                    if (transaction.type == TransactionType.Transfer) {
                        message = (
                            <>
                                Сумма клиентского счета будет уменьшена на сумму{' '}
                                <b style={{ color: 'var(--main-red)' }}>
                                    -{toFinanceString(transaction.amount)} {currencySign(transaction.currency)}
                                </b>
                                <br />
                                Все платежные операции по счетам, связанные с операцией <b>"Перевод"</b>, будут отменены.
                            </>
                        );
                    }

                    setMessageInfo(message);
                }

                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (user && user.userSettings) {
            form.setFieldsValue({
                userId: userId,
                exchangeRateCommissionType: user.userSettings.exchangeRateCommissionType || ExchangeRateCommissionType.Percentage,
                exchangeRateCommission: user.userSettings.exchangeRateCommission,
            });

            if (user.userSettings.useInternalExchangeRate) {
                setCurrentRateType(ExchangeRateType.Internal);
            }
        } else {
            form.setFieldsValue({
                userId: userId,
                exchangeRateCommissionType: ExchangeRateCommissionType.Percentage,
            });
        }
    }, [user]);

    useEffect(() => {
        form.setFieldValue(
            'exchangeRate',
            currentRateType && currentRateType == ExchangeRateType.CentralBank ? undefined : internalUsdExchangeRate
        );

        calcaulateAmount();
    }, [currentRateType]);

    const onCancelTransaction = (entity: ICancelTransactionParams) => {
        serverFetch(`transactions/cancel`, { method: 'POST', bodyData: entity })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка отмены операции', ex, () => d(userLoaded(undefined)));
            });
    };

    const calcaulateAmount = () => {
        const fromAmount = form.getFieldValue('fromAmount');

        if (!fromAmount) {
            form.setFieldValue('amount', undefined);
            return;
        }

        let exchangeRate = form.getFieldValue('exchangeRate');

        const rateCommission = form.getFieldValue('exchangeRateCommission');
        const rateCommissionType: ExchangeRateCommissionType = form.getFieldValue('exchangeRateCommissionType');

        if (rateCommission && rateCommissionType) {
            if (rateCommissionType == ExchangeRateCommissionType.Percentage) {
                exchangeRate += exchangeRate * rateCommission * 0.01;
            } else {
                exchangeRate += rateCommission;
            }
        }

        if (exchangeRate) {
            const result = fromAmount / exchangeRate;
            form.setFieldValue('amount', result);
        } else {
            form.setFieldValue('amount', fromAmount);
        }
    };

    return (
        <Modal
            destroyOnHidden={true}
            width={650}
            title={`Отмена операции #${transaction.number}`}
            open={true}
            okText='ОК'
            onOk={() => {
                form.submit();
            }}
            onCancel={() => onCancel()}
        >
            {user && (
                <Form
                    colon={false}
                    labelCol={{ span: 5 }}
                    wrapperCol={{ span: 19 }}
                    style={{ marginTop: 20 }}
                    form={form}
                    onFinish={onCancelTransaction}
                >
                    {messageInfo && (
                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Alert showIcon type='info' description={messageInfo} />
                        </Form.Item>
                    )}

                    <Form.Item hidden name='transactionId'>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='userId'>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='exchangeRateCommissionType'>
                        <Input />
                    </Form.Item>

                    <Form.Item label='Причина' name='reason' required rules={[{ required: true, message: 'Укажите причину отмены' }]}>
                        <TextArea rows={6} />
                    </Form.Item>

                    {transaction.type == TransactionType.Depositing && (
                        <>
                            <Form.Item label='Создать счет' initialValue={addBillRequired} name='addBillRequired' valuePropName='checked'>
                                <Checkbox
                                    onChange={(e) => {
                                        setAddBillRequired(e.target.checked);
                                    }}
                                />
                            </Form.Item>

                            {addBillRequired && (
                                <>
                                    <Form.Item label='Сумма' name='fromAmount' wrapperCol={{ span: 6 }} initialValue={transaction?.amount}>
                                        <InputNumber
                                            disabled={!!transaction}
                                            precision={2}
                                            decimalSeparator=','
                                            min={0}
                                            style={{ width: '170px' }}
                                            onChange={(value: number | null) => {
                                                form.setFieldValue('fromAmount', value);
                                                calcaulateAmount();
                                            }}
                                            suffix={currencySign(transaction.currency)}
                                        />
                                    </Form.Item>

                                    {transaction.currency != Currency.Usd && (
                                        <>
                                            <Form.Item
                                                initialValue={currentRateType}
                                                label='Источник курса'
                                                name='exchangeRateType'
                                                rules={[{ required: true, message: 'Укажите источник курса' }]}
                                            >
                                                <Select
                                                    onChange={(value: ExchangeRateType) => {
                                                        setCurrentRateType(value);
                                                    }}
                                                    options={rateTypes.map((a) => {
                                                        return { value: a.value, label: a.label };
                                                    })}
                                                />
                                            </Form.Item>

                                            <Form.Item label='Курс' required>
                                                <Flex align='start' gap='middle'>
                                                    <Form.Item
                                                        name='exchangeRate'
                                                        rules={[{ required: true, message: 'Укажите курс' }]}
                                                        style={{ width: '50%', marginBottom: 0 }}
                                                    >
                                                        <InputNumber
                                                            decimalSeparator=','
                                                            precision={4}
                                                            style={{ width: '100%' }}
                                                            min={0}
                                                            onChange={(value: number | null) => {
                                                                form.setFieldValue('exchangeRate', value);
                                                                calcaulateAmount();
                                                            }}
                                                        />
                                                    </Form.Item>

                                                    <PlusOutlined style={{ height: 30 }} />

                                                    <Form.Item
                                                        name='exchangeRateCommission'
                                                        style={{ width: '50%', marginBottom: 0 }}
                                                        initialValue={transaction?.exchangeRateCommission}
                                                    >
                                                        <InputNumber
                                                            precision={2}
                                                            decimalSeparator=','
                                                            min={0}
                                                            onChange={(value: number | null) => {
                                                                form.setFieldValue('exchangeRateCommission', value);
                                                                calcaulateAmount();
                                                            }}
                                                            addonAfter={
                                                                <Select
                                                                    defaultValue={
                                                                        transaction?.exchangeRateCommissionType ||
                                                                        user?.userSettings?.exchangeRateCommissionType ||
                                                                        ExchangeRateCommissionType.Percentage
                                                                    }
                                                                    onChange={(value: ExchangeRateCommissionType) => {
                                                                        form.setFieldValue('exchangeRateCommissionType', value);
                                                                        calcaulateAmount();
                                                                    }}
                                                                    options={rateCommissionTypes.map((c) => {
                                                                        return {
                                                                            value: c.value,
                                                                            label:
                                                                                c.value == ExchangeRateCommissionType.Percentage
                                                                                    ? '%'
                                                                                    : currencySign(Currency.Rub),
                                                                        };
                                                                    })}
                                                                />
                                                            }
                                                        />
                                                    </Form.Item>
                                                </Flex>
                                            </Form.Item>
                                        </>
                                    )}

                                    <Form.Item name='amount' label='ИТОГО'>
                                        <InputNumber
                                            decimalSeparator=','
                                            disabled={true}
                                            precision={2}
                                            min={0}
                                            style={{ width: '170px', background: '#f6fefa' }}
                                            suffix={currencySign(Currency.Usd)}
                                        />
                                    </Form.Item>
                                </>
                            )}
                        </>
                    )}
                </Form>
            )}
        </Modal>
    );
};

export default CancelTransactionModal;
